import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_elite-realty-40/artifacts/q2vcqxyz_%D8%AA%D8%B5%D9%85%D9%8A%D9%85%20%D8%A8%D8%AF%D9%88%D9%86%20%D8%B9%D9%86%D9%88%D8%A7%D9%86%20-%202026-06-21T092601.962.png";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate("/admin");
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "بيانات الدخول غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #0A1128 0%, #173269 60%, #0A1128 100%)" }}
      dir="rtl"
    >
      {/* Decorative circles */}
      <div className="fixed top-20 right-20 w-64 h-64 rounded-full opacity-10"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>
      <div className="fixed bottom-20 left-20 w-48 h-48 rounded-full opacity-10"
        style={{ background: "radial-gradient(circle, #ecc775, transparent)" }}></div>

      <div className="w-full max-w-md relative">
        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-10 rounded-sm shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-10">
            <img src={LOGO} alt="الوسيم العقارية" className="h-20 w-auto mx-auto object-contain mb-4" style={{ mixBlendMode: "screen" }} />
            <h1 className="text-white font-bold text-2xl">لوحة تحكم الوسيم</h1>
            <p className="text-white/50 text-sm mt-1">تسجيل دخول المدير</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="text-white/70 text-sm font-semibold mb-2 block">البريد الإلكتروني</label>
              <div className="relative">
                <Mail size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="admin-login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="أدخل البريد الإلكتروني"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 pr-11 pl-4 py-3.5 focus:outline-none focus:border-[#ecc775] transition-colors rounded-sm font-cairo"
                  dir="ltr"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-white/70 text-sm font-semibold mb-2 block">كلمة المرور</label>
              <div className="relative">
                <Lock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="admin-login-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 pr-11 pl-11 py-3.5 focus:outline-none focus:border-[#ecc775] transition-colors rounded-sm font-cairo"
                  dir="ltr"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/20 border border-red-500/40 text-red-300 px-4 py-3 rounded-sm text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#ecc775] text-[#173269] py-4 font-black text-lg hover:bg-white transition-all duration-300 disabled:opacity-60 rounded-sm shadow-lg shadow-[#ecc775]/20 mt-2"
            >
              {loading ? "جاري الدخول..." : "تسجيل الدخول"}
            </button>
          </form>

          {/* Back link */}
          <div className="text-center mt-6">
            <a href="/" className="text-white/40 text-sm hover:text-white/70 transition-colors">
              العودة إلى الموقع
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
